import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ImageRepositorySearch from './ImageRepositorySearch';
import ImageRepositoryBasket from './ImageRepositoryBasket';
import ImageRepositoryList from './ImageRepositoryList';
import {
  IImageRepositoryItem,
  IImageRepositoryItemGroup,
  IImageRepositorySearchResponse,
} from './types';
import { get } from '../../Services/http';
import { Pager, calculatePager } from '../../Services/Pagination.service';
import Pagination from '../Pagination';
import { translate } from '../../Services/translation';
import ImageRepositoryInitialScreen from './ImageRepositoryInitialScreen';
import { EditorStringModel } from '../../Types/EditorString';
import { debounce } from 'lodash-es';
import ImageRepositoryFloatingDownloadButton from './ImageRepositoryFloatingDownloadButton';

let abortController: AbortController | undefined;

interface ImageRepositoryContainerProps {
  text?: EditorStringModel;
  maxArchiveSizeBytes: number;
}

const ImageRepositoryContainer = ({
  text: initialScreenText,
  maxArchiveSizeBytes,
}: ImageRepositoryContainerProps) => {
  const [itemGroups, setItemGroups] =
    useState<IImageRepositoryItemGroup[]>();
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(0);
  const [searchText, setSearchText] = useState<string | undefined>();
  const [selectedItems, setSelectedItems] = useState<
    IImageRepositoryItem[]
  >([]);
  const [error, setError] = useState<undefined | string>();
  const [pager, setPager] = useState<Pager | undefined>();
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const [showInitialScreen, setShowInitialScreen] = useState(true);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);

  const isFileTooLarge = useMemo(
    () =>
      selectedItems.reduce((prev, item) => prev + item.fileSizeBytes, 0) >
      maxArchiveSizeBytes,
    [maxArchiveSizeBytes, selectedItems]
  );

  const onSearch = useCallback(
    debounce(async (searchText) => {
      try {
        abortController?.abort();
        abortController = new AbortController();

        const url = `/api/imagerepository?q=${searchText}&page=${currentPage}`;
        const res = await get(url, abortController);
        const data: IImageRepositorySearchResponse = await res.json();

        setItemGroups(data.itemGroups);
        setTotalCount(data.totalCount);
        setPageSize(data.pageSize);
        setShowInitialScreen(false);
        setError(undefined);

        window.scrollTo({ top: 0, behavior: 'auto' });
      } catch (err) {
        if (err?.name != 'AbortError') {
          setShowInitialScreen(false);
          setError(translate('general.error'));
          console.error(err);
        }
      } finally {
        setIsLoadingSearch(false);
        abortController = undefined;
      }
    }, 400),
    []
  );

  const onItemSelect = (selected: boolean, item: IImageRepositoryItem) => {
    setSelectedItems((selectedItems) => {
      let selectedItemsCopy = [...selectedItems];
      const itemIndex = selectedItems.findIndex(
        (x) => x.systemId == item.systemId
      );
      if (itemIndex !== -1 && !selected) {
        selectedItemsCopy.splice(itemIndex, 1);
      } else if (selected && itemIndex == -1) {
        selectedItemsCopy.push(item);
      }

      return selectedItemsCopy;
    });
  };

  useEffect(() => {
    if (!!searchText) {
      setIsLoadingSearch(true);
      onSearch(searchText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchText]);

  useEffect(() => {
    setPager(calculatePager(totalCount, currentPage, { pageSize }));
  }, [totalCount, currentPage, pageSize]);

  return (
    <div className="container mb-4 min-h-screen bg-gray-50">
      {showInitialScreen && (
        <ImageRepositoryInitialScreen
          onSearch={setSearchText}
          isLoading={isLoadingSearch}
          text={initialScreenText}
        />
      )}
      {!showInitialScreen && (
        <>
          <h1 className="mb-2">{translate('image-repository.title')}</h1>
          <div className="mb-4 grid grid-cols-1 gap-2 lg:grid-cols-2 lg:gap-4">
            <div>
              <ImageRepositorySearch
                onSearch={setSearchText}
                value={searchText}
                isLoading={isLoadingSearch}
              />
              {error && <small className="text-red-500">{error}</small>}
            </div>
            {isFileTooLarge && (
              <small className="hidden self-end text-red-500 lg:block">
                {translate('image-repository.file-too-large')}
              </small>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div>
              {itemGroups && itemGroups.length > 0 && (
                <ImageRepositoryList
                  groups={itemGroups ?? []}
                  selectedItems={selectedItems}
                  onItemSelect={onItemSelect}
                />
              )}
              {(!itemGroups || itemGroups.length == 0) && (
                <>{translate('image-repository.search.no-hits')}</>
              )}
              {pager && pager.pageCount! > 1 && (
                <div className="mt-4">
                  <Pagination model={pager} onChange={setCurrentPage} />
                </div>
              )}
            </div>
            <div>
              {isFileTooLarge && (
                <small className="self-end text-red-500 lg:hidden">
                  {translate('image-repository.file-too-large')}
                </small>
              )}
              <ImageRepositoryBasket
                items={selectedItems}
                onItemSelect={onItemSelect}
                onClearItems={() => setSelectedItems([])}
              />
            </div>
          </div>
          {!isFileTooLarge && (
            <ImageRepositoryFloatingDownloadButton
              canDownload={selectedItems.length > 0}
              isLoading={isLoadingDownload}
              onDownloadStarted={() => setIsLoadingDownload(true)}
              onDownloadFinished={() => setIsLoadingDownload(false)}
              items={selectedItems}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ImageRepositoryContainer;
